import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, Navigate, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Library,
  Database,
  Search,
  Image as ImageIcon,
  Inbox,
  Settings,
  LogOut,
  Menu,
  Sparkles,
  ExternalLink,
  Sun,
  Moon,
  BookOpen,
  HelpCircle,
  MessageSquareQuote,
  Tag,
  Globe2,
  ScrollText,
  Layers,
  Handshake,
  ShoppingBag,
  ShieldCheck,
  Users2,
  FormInput,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AdminThemeProvider, useAdminTheme, tc } from './theme';
import useAdminShortcuts, { SHORTCUT_LIST } from './useAdminShortcuts';
import './admin-light-overrides.css';

/**
 * Keyboard cheat sheet overlay — rendered when the user presses "?"
 * anywhere in the admin. Listens to the shortcut hook's open state.
 */
function ShortcutCheatSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
      role="dialog"
      aria-label="Keyboard shortcuts"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <p className="font-extrabold text-slate-900">Keyboard shortcuts</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">esc</button>
        </div>
        <ul className="space-y-2">
          {SHORTCUT_LIST.map((s) => (
            <li key={s.keys} className="flex items-center justify-between text-sm">
              <span className="text-slate-700">{s.label}</span>
              <span className="font-mono text-xs font-bold text-slate-900 bg-slate-100 border border-slate-200 px-2 py-1 rounded-md">
                {s.keys}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-slate-500 mt-4">
          Press <kbd className="font-mono bg-slate-100 px-1 rounded">?</kbd> any time to see this list.
        </p>
      </div>
    </div>
  );
}

// Role hierarchy used for nav visibility (mirrors backend auth.py).
const ROLE_RANK = { owner: 3, editor: 2, support: 1, viewer: 0 };
const rank = (r) => ROLE_RANK[r === 'admin' ? 'owner' : r] ?? 0;

// Navigation is grouped: a `divider` entry inserts a small section label.
// `minRole` hides an item below the given role rank. Content lives at
// editor+, inbox at support+, system/users at owner only. Dividers carry
// minRole too so an empty section header doesn't render.
const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/pages', label: 'Pages', icon: FileText, minRole: 'editor' },
  { to: '/admin/seo', label: 'SEO Hub', icon: Search, minRole: 'editor' },
  { to: '/admin/ai', label: 'AI Prompts', icon: Sparkles, minRole: 'editor' },
  { to: '/admin/media', label: 'Media Library', icon: ImageIcon, minRole: 'editor' },

  { divider: 'Content', minRole: 'editor' },
  { to: '/admin/blog', label: 'Blog Posts', icon: BookOpen, minRole: 'editor' },
  { to: '/admin/forms', label: 'Forms', icon: FormInput, minRole: 'editor' },
  { to: '/admin/resources', label: 'Resources', icon: Library, minRole: 'editor' },
  { to: '/admin/faqs', label: 'FAQs', icon: HelpCircle, minRole: 'editor' },
  { to: '/admin/reviews', label: 'Reviews', icon: MessageSquareQuote, minRole: 'editor' },
  { to: '/admin/logos', label: 'CRM Logos', icon: Tag, minRole: 'editor' },

  { divider: 'Master Data', minRole: 'editor' },
  { to: '/admin/virtual-pricing', label: 'Virtual Pricing', icon: Globe2, minRole: 'editor' },
  { to: '/admin/regulations', label: 'Regulations', icon: ScrollText, minRole: 'editor' },
  { to: '/admin/content', label: 'Content Blocks', icon: Layers, minRole: 'editor' },
  { to: '/admin/settings', label: 'Site Settings', icon: Settings, minRole: 'owner' },

  { divider: 'Inbox', minRole: 'support' },
  { to: '/admin/leads', label: 'Contact Leads', icon: Inbox, minRole: 'support' },
  { to: '/admin/partners', label: 'Partner Apps', icon: Handshake, minRole: 'support' },
  { to: '/admin/orders', label: 'Number Orders', icon: ShoppingBag, minRole: 'support' },

  { divider: 'System', minRole: 'owner' },
  { to: '/admin/users', label: 'Team & Access', icon: Users2, minRole: 'owner' },
  { to: '/admin/audit-log', label: 'Audit Log', icon: ShieldCheck, minRole: 'owner' },
];

function LayoutInner() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { theme, toggle } = useAdminTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const myRole = (user && user.role) || 'viewer';
  // Keyboard shortcuts ("g d" / "g l" etc.) + a "?" cheat sheet overlay.
  const { cheatOpen, setCheatOpen } = useAdminShortcuts();

  if (user === null) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${tc.pageBg(theme)} ${tc.textMuted(theme)}`}>
        Checking session…
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
      isActive ? tc.navActiveBg(theme) : tc.navIdle(theme)
    }`;

  return (
    <div className={`min-h-screen flex ${tc.pageBg(theme)} ${tc.textPrimary(theme)} ${theme === 'light' ? 'admin-theme-light' : ''}`}>
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-64 flex flex-col z-40 transform ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } transition-transform ${tc.sidebarBg(theme)}`}
        data-testid="admin-v2-sidebar"
      >
        <div className={`p-5 flex items-center gap-3 border-b ${tc.divider(theme)}`}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center font-extrabold text-white text-xl shadow-lg shadow-fuchsia-500/30">
            V
          </div>
          <div className="flex-1">
            <p className="font-extrabold leading-tight">VoIPinger</p>
            <p className={`text-[10px] uppercase tracking-widest leading-tight ${theme === 'light' ? 'text-violet-700' : 'text-violet-300/80'}`}>
              Admin <span className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}>v2</span>
            </p>
          </div>
          <button
            type="button"
            onClick={toggle}
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              theme === 'light'
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900'
                : 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
            } transition-all`}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV.filter((item) => !item.minRole || rank(myRole) >= rank(item.minRole)).map((item, idx) => {
            if (item.divider) {
              return (
                <p
                  key={`div-${idx}`}
                  className={`px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold ${tc.textDim(theme)}`}
                >
                  {item.divider}
                </p>
              );
            }
            const Ic = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={navClass}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Ic size={16} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className={`p-3 border-t ${tc.divider(theme)} space-y-1`}>
          <Link
            to="/"
            target="_blank"
            className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs ${tc.textMuted(theme)} ${tc.hoverBg(theme)} transition-all`}
          >
            <ExternalLink size={14} /> View public site
          </Link>
          <button
            onClick={logout}
            data-testid="admin-v2-logout-btn"
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium ${
              theme === 'light'
                ? 'text-rose-600 hover:bg-rose-50'
                : 'text-rose-300 hover:bg-rose-500/10 hover:text-rose-200'
            } transition-all`}
          >
            <LogOut size={14} /> Logout
          </button>
          <div className={`mt-2 px-3 py-2 rounded-xl ${tc.cardMutedBg(theme)}`}>
            <p className={`text-xs font-semibold truncate ${tc.textPrimary(theme)}`}>{user?.name || 'Admin'}</p>
            <p className={`text-[11px] truncate ${tc.textMuted(theme)}`}>{user?.email}</p>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <button
          aria-label="close menu"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className={`h-14 px-4 md:px-6 flex items-center justify-between ${tc.headerBg(theme)}`}>
          <div className="flex items-center gap-3">
            <button
              className={`lg:hidden p-2 rounded-lg ${tc.hoverBg(theme)}`}
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={18} />
            </button>
            <div className={`text-sm ${tc.textMuted(theme)}`}>
              Signed in as <span className={`font-semibold ${tc.textPrimary(theme)}`}>{user?.email}</span>
            </div>
          </div>
          <div className={`text-[10px] uppercase tracking-widest ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>
            {theme === 'light' ? 'Light' : 'Dark'} theme · admin v2
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <ShortcutCheatSheet open={cheatOpen} onClose={() => setCheatOpen(false)} />
    </div>
  );
}

export default function AdminV2Layout() {
  return (
    <AdminThemeProvider>
      <LayoutInner />
    </AdminThemeProvider>
  );
}
